import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from "lucide-react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, // For Zero Depreciation Cover
  FaTools, // For Engine & Gearbox Cover
  FaFileInvoiceDollar, // For Return to Invoice
  FaTruckPickup, // For Roadside Assistance
  FaOilCan, // For Consumables Cover
  FaCarSide, // For Tyre and Alloy Wheel Cover
  FaKey, // For Key Loss Protection
  FaCloudSunRain,
  FaUserLock,
  FaCarCrash,
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const AudiCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is zero depreciation worth it for Audi vehicles?",
      answer: "Yes, especially during the first 3 to 5 years of ownership.",
    },
    {
      question: "Can I buy insurance online for my Audi?",
      answer:
        "Absolutely. Most major insurers offer full digital onboarding and renewal.",
    },
    {
      question: "Is Audi insurance more expensive than BMW or Mercedes?",
      answer:
        "It is in the same premium bracket, though the cost varies by model.",
    },
    {
      question: "What is the typical claim settlement time?",
      answer:
        "Most insurers settle claims within 7 to 10 working days with full documentation.",
    },
    {
      question: "Do I need special insurance for electric Audi models?",
      answer:
        "Yes. EV policies account for battery cost, charging units, and electrical components.",
    },
    {
      question: "Can I transfer insurance from my older car to a new Audi?",
      answer:
        "Only the NCB (No Claim Bonus) can be transferred, not the policy itself.",
    },
    {
      question: "What if I modify my Audi?",
      answer:
        "Inform your insurer about any significant aftermarket modifications to avoid claim rejections.",
    },
    {
      question: "Are Audi accessories covered under standard insurance?",
      answer:
        "Factory-fitted ones usually are. Aftermarket parts need to be declared.",
    },
    {
      question: "Do I get cashless repair options for Audi cars?",
      answer:
        "Yes, most luxury-friendly insurers offer cashless services at authorized Audi centers.",
    },
    {
      question: "Can I insure a used Audi?",
      answer:
        "Yes, subject to inspection by the insurer if the car is older or previously uninsured.",
    },
    {
      question: "Is engine protection add-on necessary?",
      answer: "Highly recommended for high-performance turbocharged engines.",
    },
    {
      question: "Are there special insurance plans for RS or S-line Audis?",
      answer:
        "Not separate plans, but high-IDV and add-on-rich policies are advised.",
    },
    {
      question: "Will I lose NCB if I switch insurers?",
      answer:
        "No, NCB is portable across insurers as long as the owner remains the same.",
    },
    {
      question: "Does Audi offer its own insurance policy?",
      answer:
        "Audi dealerships sometimes tie-up with select insurers for bundled offers.",
    },
    {
      question: "Are tyres and rims covered by default?",
      answer:
        "No, but you can add a tyre protection add-on for comprehensive coverage.",
    },
    {
      question: "What if my Audi is declared a total loss?",
      answer:
        "Return to Invoice cover ensures you recover the original invoice amount.",
    },
    {
      question: "Do I need to submit physical documents for renewal?",
      answer: "No, digital submission is accepted by most insurers today.",
    },
    {
      question:
        "Is there a discount for bundling multiple Audis under one policy?",
      answer:
        "Yes, some insurers offer multi-vehicle discounts for premium clients.",
    },
    {
      question: "What’s the best add-on for city driving?",
      answer:
        "Zero depreciation and key protection are ideal for frequent urban use.",
    },
    {
      question: "Do insurance companies cover Audi EV chargers?",
      answer: "Yes, but it must be declared during policy purchase.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation Cover",
      description:
        "Ensures full claim value for parts replaced during repairs.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine & Gearbox Cover",
      description: "Useful for Quattro drivetrains and turbocharged engines.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Return to Invoice",
      description:
        "Important for new car buyers to recover full invoice value.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Tyre and Alloy Wheel Cover",
      description: "Premium wheels are expensive to repair or replace.",
      icon: <FaCarSide className="text-4xl text-indigo-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description: "Essential for intercity travel or remote breakdowns.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description:
        "Includes small but costly repair materials like coolant, oil, etc.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
    {
      title: "Key Loss Protection",
      description: "Audi smart keys are expensive to duplicate or replace.",
      icon: <FaKey className="text-4xl text-purple-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: "Easy to Use on Any Device" },
    { icon: "💰", text: "Helps You Save on Insurance" },
  ];
const audiinsurance = [
    {
      name: "A4",
      path: "/audi-a4-car-insurance", 
      image: "/assets/audia4.jpeg",
    },
    {
      name: "A6",
      path: "/audi-a6-car-insurance",
      image: "/assets/audia6.jpeg",
    },
    {
      name: "A8L",
      path: "/audi-a8l-car-insurance",
      image: "/assets/audia8l.jpeg",
    },
    {
      name: "Q3",
      path: "/audi-q3-car-insurance",
      image: "/assets/audiq3.jpeg",
    },
    {
      name: "Q5",
      path: "/audi-q5-car-insurance",
      image: "/assets/audiq5.jpeg",
    },
      {
      name: "Q7",
      path: "/audi-q7-car-insurance",
      image: "/assets/audiq7.jpeg",
    },
     {
      name: "Q8",
      path: "/audi-q8-car-insurance",
      image: "/assets/audiq8.jpeg",
    },
       {
      name: "E-tron",
      path: "/audi-etron-car-insurance",
      image: "/assets/etron.jpeg",
    },
  ];
  return (
    <>
      <Helmet>
        <title>Audi Car Insurance in India – Compare Quotes & Buy Online</title>
        <meta
          name="description"
          content="Explore Audi car insurance options in India. Compare quotes, customize coverage, and buy online to protect your Audi vehicle."
        />
        <meta
          name="keywords"
          content="audi car insurance, luxury car insurance india, audi a4 insurance, audi q7 insurance, audi e-tron insurance, car insurance quotes"
        />
        <link
          rel="canonical"
          href="https://thefinmaster.com/audi-car-insurance"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
       
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Audi Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">
                    Audi Car Insurance 
                  </span>{" "}
                
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Audi combines luxury, advanced technology, and performance to deliver an exceptional driving experience. Whether you own a stylish A4 or a commanding Q7, protecting your investment starts with the right insurance plan. This guide takes you through everything you need to know about insuring an Audi, from average premiums and model insights to useful add-ons, renewal tips, and frequently asked questions.

                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/audiinsurance.jpeg"
                alt="Audi Car"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About Audi in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Audi has established itself as a leading premium car manufacturer in
            India. With a strong product portfolio that includes luxury sedans,
            SUVs, sports cars and electric vehicles, Audi caters to a discerning
            audience that values performance, design, and brand prestige.
            Popular among entrepreneurs, professionals, and auto enthusiasts,
            Audi continues to expand its reach through new launches and
            digital-first ownership experiences.{" "}
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Some of the best-selling Audi models in India include the A4, A6,
            Q3, Q5, Q7, and e-tron. The brand also offers high-performance
            versions under the ‘S’ and ‘RS’ badging. Given the technology and
            value associated with these vehicles, a comprehensive insurance
            policy is essential for both financial protection and ease of
            ownership.
          </p>
        </div>
      </div>
         <div className="p-4 sm:p-8 bg-white">
                    <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                      Choose Your Car Brand
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                      {audiinsurance.map((calc, index) => (
                        <motion.div
                          key={index}
                          whileHover={{ scale: 1.04 }}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                        >
                          <Link
                            to={calc.path}
                            className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                          >
                            <img
                              src={calc.image}
                              alt={calc.name}
                              className="w-16 h-16 object-contain mb-2"
                            />
                            <span className="text-md font-medium text-gray-800 break-words leading-snug">
                              {calc.name}
                            </span>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Audi Owners Need Tailored Car Insurance
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Audi vehicles come with premium interiors, Quattro all-wheel-drive
            technology, advanced safety systems, and high-performance engines.
            These features are built for comfort and performance, but they also
            lead to higher repair costs. Here’s why you need robust insurance
            for your Audi:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>
                Financial protection against accidents, theft, and natural
                disasters
              </span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>
                Access to genuine parts and trained service professionals
              </span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Cashless repairs at authorized Audi service centers</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>
                Extended support during highway travel or long-distance drives
              </span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>
                Legal coverage in case of third-party damage or injuries
              </span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            A good insurance plan not only protects your car but also ensures
            seamless service and peace of mind.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no
              paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>
                  {item.icon}
                </span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Audi Models and Estimated Insurance Premiums
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Below is a reference table with approximate IDV and insurance premiums
          for popular Audi models in India:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              {
                model: "A4",
                category: "Luxury Sedan",
                idv: "50–65 Lakh",
                premium: "₹38,000 to ₹52,000",
              },
              {
                model: "A6",
                category: "Executive Sedan",
                idv: "70–85 Lakh",
                premium: "₹45,000 to ₹65,000",
              },
              {
                model: "Q3",
                category: "Compact Luxury SUV",
                idv: "55–70 Lakh",
                premium: "₹42,000 to ₹58,000",
              },
              {
                model: "Q5",
                category: "Mid-size SUV",
                idv: "70–85 Lakh",
                premium: "₹48,000 to ₹65,000",
              },
              {
                model: "Q7",
                category: "Full-size SUV",
                idv: "90 Lakh–1.2 Cr",
                premium: "₹60,000 to ₹82,000",
              },
              {
                model: "Q8 / Q8 e-tron",
                category: "Flagship SUV / EV",
                idv: "1.3 Cr–1.8 Cr",
                premium: "₹75,000 to ₹1.2 Lakh",
              },
              {
                model: "RS Models (RS5, RS Q8)",
                category: "Performance Line",
                idv: "1.2 Cr–2.5 Cr+",
                premium: "₹85,000 to ₹1.5 Lakh",
              },
              {
                model: "e-tron / e-tron GT",
                category: "Electric Vehicles",
                idv: "1.2 Cr–2 Cr+",
                premium: "₹80,000 to ₹1.4 Lakh",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="border mb-4 p-4 rounded-lg bg-white shadow-md"
              >
                <h3 className="font-semibold text-gray-800 mb-2">
                  {item.model}
                </h3>
                <div className="text-gray-600 mb-1">
                  <strong>Category:</strong> {item.category}
                </div>
                <div className="text-gray-600 mb-1">
                  <strong>Approx. IDV:</strong> {item.idv}
                </div>
                <div className="text-gray-600">
                  <strong>Starting Premium:</strong> {item.premium}
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">
                    Model
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Category
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Approx. IDV (₹)
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Starting Premium (₹)
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["A4", "Luxury Sedan", "50–65 Lakh", "₹38,000 to ₹52,000"],
                  ["A6", "Executive Sedan", "70–85 Lakh", "₹45,000 to ₹65,000"],
                  [
                    "Q3",
                    "Compact Luxury SUV",
                    "55–70 Lakh",
                    "₹42,000 to ₹58,000",
                  ],
                  ["Q5", "Mid-size SUV", "70–85 Lakh", "₹48,000 to ₹65,000"],
                  [
                    "Q7",
                    "Full-size SUV",
                    "90 Lakh–1.2 Cr",
                    "₹60,000 to ₹82,000",
                  ],
                  [
                    "Q8 / Q8 e-tron",
                    "Flagship SUV / EV",
                    "1.3 Cr–1.8 Cr",
                    "₹75,000 to ₹1.2 Lakh",
                  ],
                  [
                    "RS Models (RS5, RS Q8)",
                    "Performance Line",
                    "1.2 Cr–2.5 Cr+",
                    "₹85,000 to ₹1.5 Lakh",
                  ],
                  [
                    "e-tron / e-tron GT",
                    "Electric Vehicles",
                    "1.2 Cr–2 Cr+",
                    "₹80,000 to ₹1.4 Lakh",
                  ],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Recommended Add-Ons for Audi Car Insurance
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Audi’s high-performance engines and advanced systems make certain
          add-ons vital for complete coverage:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Top Insurers for Audi Car Insurance in India
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Not all insurance providers handle luxury cars with equal efficiency.
          Here are top insurers that offer quality service and strong add-on
          support for Audi owners:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              HDFC ERGO
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Trusted by luxury car owners for quick claims and wide network.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Tata AIG
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Popular for its digital tools and zero-dep bundled plans.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              ICICI Lombard
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Offers high IDV limits and smooth claim processes.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Bajaj Allianz
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Known for tailored luxury vehicle plans.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📱</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ACKO</h3>
            <p className="text-gray-700 text-sm mt-1">
              A digital-first insurer that suits new-age customers and EVs.
            </p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Tips to Save on Audi Insurance Premium
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Luxury car insurance can be optimized using these strategies:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Compare quotes from multiple insurers before purchase or
                  renewal.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Avoid frequent small claims to retain your No Claim Bonus
                  (NCB).
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Consider a higher voluntary deductible if you drive safely.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Install GPS and anti-theft devices.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Opt for long-term policies (3 years) for better rates.
                </span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Audi Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              To buy or renew Audi insurance, you will generally need:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Copy of RC (Registration Certificate)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Existing insurance documents (for renewal)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Aadhaar and PAN</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Valid PUC certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>Passport-size photograph (in some offline cases)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Audi Car Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Owning an Audi is a statement of style and performance. Whether
                you're driving a sleek A4 or the tech-loaded Q8 e-tron, your car
                deserves insurance that matches its prestige. With the right
                policy and add-ons, you can ensure peace of mind, quick claims,
                and access to premium service support.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we help you compare plans, pick the right
                insurer and customize your policy to match your lifestyle,
                vehicle usage, and long-term protection goals.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default AudiCarInsurance;
